import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { createPageUrl } from "@/utils";

export default function TrialBanner() {
  const navigate = useNavigate();
  const [creditsAvailable, setCreditsAvailable] = useState(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCreditsInfo = useCallback(async () => {
    try {
      const [{ data: credits }, subResult] = await Promise.all([
        api.credits.balance(),
        api.subscriptions.getStatus().catch(() => ({ data: null })),
      ]);
      const sub = subResult?.data;

      setCreditsAvailable(credits?.total_available ?? credits?.balance ?? 0);

      if (sub?.status === "trialing" && sub?.trial_end) {
        const trialEnd = new Date(sub.trial_end);
        const now = new Date();
        const daysRemaining = Math.max(
          0,
          Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        );
        setTrialDaysRemaining(daysRemaining);
      } else {
        setTrialDaysRemaining(null);
      }
    } catch {
      // keep last known values on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCreditsInfo();
    const interval = setInterval(loadCreditsInfo, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadCreditsInfo]);

  useEffect(() => {
    const onSpent = () => loadCreditsInfo();
    window.addEventListener("credits:spent", onSpent);
    return () => window.removeEventListener("credits:spent", onSpent);
  }, [loadCreditsInfo]);

  if (loading && creditsAvailable === null) return null;

  return (
    <div className="mx-3 mb-4 p-3 bg-gray-50 border border-ad-border rounded-xl">
      <p className="text-xs font-semibold text-ad-text">
        <span className="text-emerald-500">●</span>{" "}
        {creditsAvailable ?? 0} credits
      </p>
      {trialDaysRemaining != null && (
        <p className="text-[10px] text-ad-label mt-1">
          Trial · {trialDaysRemaining} day{trialDaysRemaining !== 1 ? "s" : ""} left
        </p>
      )}
      <button
        onClick={() => navigate(createPageUrl("billing/plans"))}
        className="mt-2 text-[10px] font-semibold text-ad-primary hover:text-ad-primary-hover flex items-center gap-0.5 transition-colors"
      >
        Upgrade →
      </button>
    </div>
  );
}
